/**
 * Visualization Registry
 * 
 * Maps visualization types to renderer functions.
 * Provides fallback renderer for unsupported types.
 */

import type { VisualizationType, VisualizationMeta, LayoutProfile } from '../../types/visualization.js';
import { renderTableView } from './renderers/table-view.js';
import { renderTableViewEnhanced } from './renderers/table-view-enhanced.js';
import { renderHistogram } from './renderers/histogram.js';
import { renderEisenhowerMatrix } from './renderers/eisenhower-matrix.js';
import { renderValueFrequencyBar } from './renderers/value-frequency-bar.js';
import { renderRiskMatrix } from './renderers/risk-matrix.js';
import { renderPatternDetectionRadar } from './renderers/pattern-detection-radar.js';
import { renderOutlierScatter } from './renderers/outlier-scatter.js';
import { renderAnomalyScoreBar } from './renderers/anomaly-score-bar.js';
import { renderStockChart } from './renderers/stock-chart.js';
import { renderFallback } from './renderers/fallback.js';

/**
 * Renderer function signature
 * Takes data, optional metadata, and layout profile/dimensions, returns HTML string
 */
export type VisualizationRenderer = (
  data: any,
  meta?: VisualizationMeta,
  layout?: LayoutProfile | string | { width: number; height: number }
) => string;

/**
 * Registry mapping visualization types to renderer functions
 */
const registry: Map<string, VisualizationRenderer> = new Map();

/**
 * Register a visualization renderer
 */
export function registerRenderer(
  type: VisualizationType | string,
  renderer: VisualizationRenderer
): void {
  registry.set(type, renderer);
}

/**
 * Get a renderer for a visualization type
 * Returns fallback renderer if type is not registered
 */
export function getRenderer(type: VisualizationType | string): VisualizationRenderer {
  const renderer = registry.get(type);
  if (renderer) {
    return renderer;
  }
  return renderFallback;
}

/**
 * Check if a renderer exists for a visualization type
 */
export function hasRenderer(type: VisualizationType | string): boolean {
  return registry.has(type);
}

/**
 * Initialize default renderers
 * Called during module load
 */
function initializeRenderers(): void {
  // Register all implemented renderers
  registerRenderer('TABLE_VIEW', renderTableViewEnhanced); // Using enhanced version with Hypersort features
  registerRenderer('TABLE_VIEW_BASIC', renderTableView); // Basic version available as fallback
  registerRenderer('HISTOGRAM', renderHistogram);
  registerRenderer('EISENHOWER_MATRIX', renderEisenhowerMatrix);
  registerRenderer('VALUE_FREQUENCY_BAR', renderValueFrequencyBar);
  registerRenderer('RISK_MATRIX', renderRiskMatrix);
  
  // Pattern detection visualizations
  registerRenderer('PATTERN_DETECTION_RADAR', renderPatternDetectionRadar);
  registerRenderer('OUTLIER_SCATTER', renderOutlierScatter);
  registerRenderer('ANOMALY_SCORE_BAR', renderAnomalyScoreBar);
  
  // Financial visualizations
  registerRenderer('STOCK_CHART', renderStockChart);
  
  // Register calculator sequence renderer to suppress fallback warnings
  // The calculator process handles this signal separately, but the visualization view
  // might still try to render it if it catches the signal.
  // By registering a placeholder, we avoid the "Unsupported Visualization" message
  // if the visualization process picks it up.
  registerRenderer('CALCULATOR_SEQUENCE', () => {
    return `<div style="padding: 20px; text-align: center; color: rgba(255,255,255,0.7);">
      <div style="font-size: 14px; margin-bottom: 8px;">Calculator Sequence Active</div>
      <div style="font-size: 12px;">Check the Calculator view for playback.</div>
    </div>`;
  });
  
  // Fallback is always available via getRenderer()
}

// Initialize on module load
initializeRenderers();
